/**
 * Analytics configuration constants.
 * All IDs come from environment variables — no tracking fires if they are unset.
 */

/**
 * Google Tag Manager **web** container ID (e.g. `GTM-XXXXXXX`).
 *
 * GA4 + ad-platform activation (including Google Ads conversions and Meta /
 * Reddit / Criteo) runs through GTM, so the storefront loads this container and
 * emits events onto the dataLayer (see `datalayer.ts`). The GA4 measurement ID,
 * Google Ads IDs, and enhanced-conversion config all live in the container — not
 * in env here. The value is environment-specific. Unset = GTM does not load.
 */
export const GTM_CONTAINER_ID = process.env.NEXT_PUBLIC_GTM_CONTAINER_ID ?? '';
export const POSTHOG_TOKEN = process.env.NEXT_PUBLIC_POSTHOG_TOKEN ?? '';

/**
 * Braze Web SDK API key (public, safe to embed — Braze scopes it to a
 * single workspace and rate-limits by origin). One value per deployment,
 * different keys for `c4u` and `myp` are set per brand at deploy time.
 */
export const BRAZE_API_KEY = process.env.NEXT_PUBLIC_BRAZE_API_KEY ?? '';

/**
 * Braze SDK endpoint for the EU `fra-02` cluster. Matches the REST
 * endpoint used by the server-side connector (`rest.fra-02.braze.eu`)
 * so client- and server-side data land in the same workspace.
 *
 * Override via `NEXT_PUBLIC_BRAZE_SDK_ENDPOINT` if a brand is migrated
 * to a different cluster.
 */
export const BRAZE_SDK_ENDPOINT =
  process.env.NEXT_PUBLIC_BRAZE_SDK_ENDPOINT ?? 'sdk.fra-02.braze.eu';

/**
 * Debug mode: set NEXT_PUBLIC_ANALYTICS_DEBUG=true to show the consent banner,
 * render tracker components, and log all GA4 events to the browser console
 * without needing real GA credentials.
 */
export const ANALYTICS_DEBUG =
  process.env.NEXT_PUBLIC_ANALYTICS_DEBUG === 'true';

/** localStorage key for persisted consent preferences. */
export const CONSENT_STORAGE_KEY = 'cookie_consent_preferences';

/**
 * Cookie name for consent level.
 *
 * Mirrors the localStorage consent choice as a simple cookie so that
 * Server Components can read consent status without client-side JS.
 * Values: `"all"` (analytics + advertising granted) or `"essential"`
 * (non-essential cookies declined). Absence means no choice made yet.
 */
export const CONSENT_COOKIE_NAME = '_consent';

/** Whether a GTM web container is configured (controls whether gtm.js loads). */
export const IS_GTM_CONFIGURED = GTM_CONTAINER_ID.length > 0;

/**
 * Whether analytics is configured (has a GTM container or PostHog token) OR
 * debug mode is on. Internal — feeds `IS_CONSENT_REQUIRED`. Components gate the
 * GTM loader on `IS_GTM_CONFIGURED` directly.
 */
const IS_ANALYTICS_CONFIGURED =
  IS_GTM_CONFIGURED || POSTHOG_TOKEN.length > 0 || ANALYTICS_DEBUG;

/** Whether Braze Web SDK is configured (has an API key). */
export const IS_BRAZE_CONFIGURED = BRAZE_API_KEY.length > 0;

/**
 * Custom DOM event name dispatched when analytics consent is granted.
 * The GoogleAnalytics component listens for this to dynamically load gtag.js.
 */
export const CONSENT_GRANTED_EVENT = 'analytics:consent-granted';

/**
 * Custom DOM event dispatched on every consent preference change
 * (accept, reject, or custom save). Components subscribe to this to
 * reactively update consent-gated behaviour.
 */
export const CONSENT_UPDATED_EVENT = 'analytics:consent-updated';

/**
 * Custom DOM event dispatched when **marketing (advertising) consent**
 * is granted. The `Braze` SDK loader listens for this — Braze is gated
 * on marketing consent, not analytics consent, per the most-compliant
 * interpretation of MWP-449.
 */
export const MARKETING_CONSENT_GRANTED_EVENT =
  'analytics:marketing-consent-granted';

/**
 * Custom DOM event dispatched once the Braze Web SDK has been
 * dynamically imported and `setBrazeInstance` has registered the
 * instance. `BrazeIdentity` listens for this so it can call
 * `brazeIdentify` for an already-authenticated customer once the
 * async SDK load completes (the consent-granted event fires
 * synchronously but the import is async, so the two states are
 * racy).
 */
export const BRAZE_LOADED_EVENT = 'analytics:braze-loaded';

/**
 * Custom DOM event name dispatched to open the cookie settings modal.
 * The footer "Cookie Settings" button and the `/cookie-settings` page
 * dispatch this; the CookieConsent component listens for it.
 */
export const OPEN_COOKIE_SETTINGS_EVENT = 'analytics:open-cookie-settings';

/**
 * Whether the cookie consent banner is required to be shown.
 *
 * True when ANY consent-requiring vendor is configured. Used by the
 * CookieConsent banner so a Braze-only deployment still surfaces the banner —
 * not just GTM / PostHog deployments. Without this, new visitors to a Braze-only
 * site never get a chance to grant marketing consent and the Braze SDK never
 * loads. (Google Ads + Meta now live inside the GTM container, so they're
 * covered by `IS_GTM_CONFIGURED` via `IS_ANALYTICS_CONFIGURED`.)
 */
export const IS_CONSENT_REQUIRED =
  IS_ANALYTICS_CONFIGURED || IS_BRAZE_CONFIGURED;

/**
 * URL path prefixes that are known NOT to contain health-identifying data.
 *
 * Any root-level path whose first segment is NOT in this set could be a
 * product or category page (e.g. `/sildenafil-50mg`, `/erectile-dysfunction`).
 * When analytics (Statistics) consent is not granted, such paths are replaced
 * with a generic placeholder in analytics to prevent product/condition handles
 * leaking (e.g. in Advanced Consent Mode cookieless pings).
 *
 * Keep aligned with proxy bypasses/static-route handling in `src/proxy.ts`
 * where applicable, but this list is analytics-specific and does not need to
 * exactly match `STATIC_ROUTES`.
 */
export const SAFE_PATH_PREFIXES = new Set([
  'basket',
  'checkout',
  'account',
  'search',
  'consultation',
  'order',
  'faqs',
  'how-it-works',
  'about',
  'news',
  'contact',
  'auth',
  'cookie-settings',
  'cookie-policy',
  'content',
  'conditions', // /conditions listing page
  'ingest', // PostHog proxy
  'healthz',
  '_next', // Next.js internals
  'api', // API routes
]);

/** Placeholder path used when scrubbing product/category URLs. */
export const SCRUBBED_PATH = '/[redacted]';

/**
 * Cookie set by Playwright in the browser context to signal that the page is
 * running under an E2E test. Both client-side and server-side Sentry configs
 * check for this cookie in `beforeSend` and drop all events when it is present,
 * preventing test noise from polluting Sentry.
 *
 * The Playwright side must set this cookie in `playwright.config.ts`:
 *   use: { storageState: { cookies: [{ name: PLAYWRIGHT_E2E_COOKIE, value: '1', ... }] } }
 * or in a global setup / `beforeEach` via `context.addCookies(...)`.
 */
export const PLAYWRIGHT_E2E_COOKIE = '_playwright_e2e';
